import axios from 'axios';
import { ApiError } from '@/shared/types/api.types';
import type { ApiErrorKind, ApiValidationIssue, ProblemDetailsResponse } from '@/shared/types/api.types';

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

function isProblemDetails(value: unknown): value is ProblemDetailsResponse {
  return typeof value === 'object' && value !== null;
}

/**
 * OAuth 2.0 error response (RFC 6749 §5.2), which is what IdentityServer4's token
 * endpoint returns. It carries `error` / `error_description` rather than the
 * `ProblemDetails` shape the rest of the API uses, so a failed sign-in would otherwise
 * fall back to a generic message and hide the reason from the user.
 */
interface OAuthErrorResponse {
  readonly error?: string;
  readonly error_description?: string;
}

function oauthMessage(body: unknown): string | null {
  if (!isProblemDetails(body)) return null;

  const oauth = body as OAuthErrorResponse;
  if (typeof oauth.error_description === 'string' && oauth.error_description.length > 0) {
    return oauth.error_description;
  }

  // `error` is a machine-readable code such as `invalid_grant`; only worth showing when
  // the server sent no human-readable description alongside it.
  if (typeof oauth.error === 'string' && oauth.error.length > 0) {
    return OAUTH_ERROR_MESSAGES[oauth.error] ?? null;
  }

  return null;
}

const OAUTH_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  invalid_grant: 'Incorrect username or password.',
  invalid_client: 'This application is not registered with the identity server.',
  invalid_scope: 'This application requested permissions it is not allowed to use.',
  unauthorized_client: 'This application is not permitted to sign users in.',
  unsupported_grant_type: 'The identity server rejected this sign-in method.',
};

function isFieldErrorMap(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractIssues(body: unknown): readonly ApiValidationIssue[] {
  if (!isProblemDetails(body) || !isFieldErrorMap(body.errors)) return [];

  return Object.entries(body.errors).map(([field, messages]) => ({
    field,
    messages: Array.isArray(messages) ? messages.map(String) : [String(messages)],
  }));
}

function extractCode(body: unknown): string | undefined {
  if (!isProblemDetails(body)) return undefined;
  const { code } = body;
  return typeof code === 'string' && code.length > 0 ? code : undefined;
}

function extractMessage(body: unknown, fallback: string): string {
  if (typeof body === 'string' && body.trim().length > 0) return body;

  const oauth = oauthMessage(body);
  if (oauth !== null) return oauth;

  if (isProblemDetails(body)) {
    if (typeof body.detail === 'string' && body.detail.length > 0) return body.detail;
    if (typeof body.title === 'string' && body.title.length > 0) return body.title;
  }

  return fallback;
}

/**
 * Collapses anything thrown by Axios into an {@link ApiError}. The Core service returns
 * a mix of `ProblemDetails`, bare strings (`return StatusCode(500, "...")`), and empty
 * bodies, so all three are handled here rather than in each feature.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError('timeout', 'The request timed out. Please try again.', { cause: error });
    }

    const { response } = error;
    if (response === undefined) {
      return new ApiError('network', 'Unable to reach the server. Check your connection.', {
        cause: error,
      });
    }

    const kind = kindForStatus(response.status);
    const body: unknown = response.data;

    return new ApiError(kind, extractMessage(body, defaultMessageFor(kind)), {
      status: response.status,
      issues: extractIssues(body),
      code: extractCode(body),
      cause: error,
    });
  }

  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
  return new ApiError('unknown', message, { cause: error });
}

function defaultMessageFor(kind: ApiErrorKind): string {
  switch (kind) {
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'forbidden':
      return 'You do not have permission to perform this action.';
    case 'notFound':
      return 'The requested item could not be found.';
    case 'conflict':
      return 'This action conflicts with the current state of the item.';
    case 'validation':
      return 'Some of the submitted values are invalid.';
    case 'server':
      return 'The server encountered an error. Please try again later.';
    case 'network':
      return 'Unable to reach the server.';
    case 'timeout':
      return 'The request timed out.';
    case 'unknown':
      return 'An unexpected error occurred.';
  }
}
